import {analytics} from './analytics';
import {patientService} from './patientService';
import {ehrService, ehrTemplateToScribeTemplate} from './ehrService';
import {mergeAllTemplates} from './scribeService';

// ── In-flight + short-TTL result cache ───────────────────────────
//
// Multiple views (`ScribePage`, `SessionDetailPanel`, `PostSessionNotesView`,
// `ToolsTabContent`) each call `loadResolvedNoteTemplates`
// independently on mount. Before this cache, a navigation flow like
// "session detail → Create Clinical Note → /process" issued two
// back-to-back `/note-templates` requests. If the second one tripped a
// transient backend hiccup (we've seen `[ehr_user_sync_no_match]` warnings
// from the EHR backend during the same window), the second view silently
// fell back to the built-in default templates — even though the first
// view had successfully resolved the EHR template list a second earlier.
//
// Two layers, both keyed by `userId:companyId`:
//   1. `inFlight` — dedupes overlapping calls inside the same tick so
//      route transitions that fire two loads at once reuse the same
//      network request.
//   2. `resultCache` — caches *successful* results for `CACHE_TTL_MS`. We
//      deliberately do NOT cache failures so a transient EHR API blip
//      doesn't get pinned for the whole TTL; the next caller will retry.
//
// The TTL is short enough that genuine page revisits (a few minutes
// apart) still pick up template additions/removals and EhrConfig
// status changes, while back-to-back navigations within a single
// workflow are coalesced.
const CACHE_TTL_MS = 2 * 60 * 1000;
const inFlight = new Map<string, Promise<any>>();
const resultCache = new Map<string, {value: any; cachedAt: number}>();

function cacheKey(
  userId: string | null | undefined,
  companyId: string | null | undefined,
): string {
  return `${userId || ''}:${companyId || ''}`;
}

async function fetchEhrTemplatesWithRetry(
  maxAttempts: number = 3,
): Promise<any> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await ehrService.fetchNoteTemplates();
    } catch (err) {
      if (attempt === maxAttempts) {
        throw err;
      }
      await new Promise(resolve => setTimeout(resolve, 800 * attempt));
    }
  }
  return {ehrEnabled: false, templates: []};
}

export async function loadResolvedNoteTemplates({
  userId,
  companyId,
  source = 'note-templates',
  force = false,
}: {
  userId?: string | null;
  companyId?: string | null;
  source?: string;
  force?: boolean;
}): Promise<any> {
  if (!userId) {
    return {
      customTemplates: [],
      ehrTemplates: [],
      ehrConfig: null,
      ehrEnabledFromApi: false,
      hasActiveEhr: false,
      ehrTemplatesFailed: false,
      ehrTemplatesError: null,
      allTemplates: mergeAllTemplates([], []),
      fromCache: false,
    };
  }

  const key = cacheKey(userId, companyId);

  if (!force) {
    const cached = resultCache.get(key);
    if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
      return {...cached.value, fromCache: true};
    }

    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const promise = (async () => {
    const customPromise = patientService
      .fetchAllTemplates(userId, companyId || null)
      .catch(err => {
        console.error(`[${source}] Error loading custom templates:`, err);
        return [];
      });

    const ehrPromise = (async () => {
      if (!companyId) {
        // A signed-in user always belongs to a company in this app, so a
        // missing companyId means "user record hasn't finished loading"
        // — NOT "this company has no EHR". Returning a clean no-EHR result
        // here is what lets a half-loaded mount silently fall back to the
        // built-in default templates (the "default templates shown even
        // though the org has EHR templates" bug). Flag it as failed so
        // callers surface the retry/loading state instead of presenting
        // generic defaults, and so it never gets cached as a good result.
        return {
          ehrConfig: null,
          ehrTemplates: [],
          ehrEnabledFromApi: false,
          ehrTemplatesFailed: true,
          ehrTemplatesError: 'Company not resolved yet',
        };
      }

      let ehrConfig: any = null;
      let configActive = false;
      try {
        ehrConfig = await ehrService.fetchEhrConfig(companyId);
        configActive = ehrService.isConfigActive(ehrConfig);
      } catch (err) {
        // Couldn't even read EhrConfig — treat as "EHR unknown / failed".
        console.error(`[${source}] Error loading EhrConfig:`, err);
        return {
          ehrConfig: null,
          ehrTemplates: [],
          ehrEnabledFromApi: false,
          ehrTemplatesFailed: true,
          ehrTemplatesError: err?.message || 'Failed to read EHR config',
        };
      }

      if (!configActive) {
        // Legitimately no active EHR for this company — not a failure.
        return {
          ehrConfig,
          ehrTemplates: [],
          ehrEnabledFromApi: false,
          ehrTemplatesFailed: false,
          ehrTemplatesError: null,
        };
      }

      try {
        const tmplResp = await fetchEhrTemplatesWithRetry();
        const ehrEnabledFromApi = Boolean(tmplResp.ehrEnabled);
        const templates = ehrEnabledFromApi ? tmplResp.templates || [] : [];
        // EhrConfig says active, but backend says no templates / disabled —
        // this is the "EHR is configured but something is wrong" branch.
        const failed = !ehrEnabledFromApi || templates.length === 0;
        return {
          ehrConfig,
          ehrTemplates: templates,
          ehrEnabledFromApi,
          ehrTemplatesFailed: failed,
          ehrTemplatesError: failed
            ? ehrEnabledFromApi
              ? 'EHR templates returned empty'
              : 'EHR templates disabled by backend'
            : null,
        };
      } catch (err) {
        console.error(`[${source}] Error loading EHR templates:`, err);
        return {
          ehrConfig,
          ehrTemplates: [],
          ehrEnabledFromApi: false,
          ehrTemplatesFailed: true,
          ehrTemplatesError: err?.message || 'Failed to fetch EHR templates',
        };
      }
    })();

    const [customTemplates, ehrResult] = await Promise.all([
      customPromise,
      ehrPromise,
    ]);
    const allTemplates = mergeAllTemplates(
      customTemplates,
      ehrResult.ehrTemplates.map(ehrTemplateToScribeTemplate),
    );

    const hasActiveEhr =
      ehrService.isConfigActive(ehrResult.ehrConfig) ||
      ehrResult.ehrEnabledFromApi;

    // Surface degraded loads to analytics so we can detect them in
    // aggregate. Only fires when EHR was *expected* (EhrConfig active)
    // but we couldn't actually produce a usable template list.
    if (
      ehrResult.ehrTemplatesFailed &&
      ehrService.isConfigActive(ehrResult.ehrConfig)
    ) {
      try {
        analytics.capture('ehr_templates_load_failed', {
          source,
          companyId: companyId || null,
          error: ehrResult.ehrTemplatesError,
          ehrEnabledFromApi: ehrResult.ehrEnabledFromApi,
          template_count: ehrResult.ehrTemplates.length,
        });
      } catch (_) {
        /* analytics best-effort */
      }
    }

    return {
      customTemplates,
      ehrTemplates: ehrResult.ehrTemplates,
      ehrConfig: ehrResult.ehrConfig,
      ehrEnabledFromApi: ehrResult.ehrEnabledFromApi,
      hasActiveEhr,
      ehrTemplatesFailed: ehrResult.ehrTemplatesFailed,
      ehrTemplatesError: ehrResult.ehrTemplatesError,
      allTemplates,
      fromCache: false,
    };
  })();

  inFlight.set(key, promise);

  try {
    const value = await promise;
    // Cache only when the load was clean — caching a failure would pin
    // the bad state for the whole TTL and prevent the next caller from
    // retrying.
    if (!value.ehrTemplatesFailed) {
      resultCache.set(key, {value, cachedAt: Date.now()});
    }
    return value;
  } finally {
    if (inFlight.get(key) === promise) inFlight.delete(key);
  }
}

// ── Composed-view template block ─────────────────────────────────
//
// The session-detail / scribe-start view endpoints return the resolved
// template state ({custom, ehr, ehrConfig, ehrEnabled}) in the same payload
// as the rest of the view, so views that mount from them don't issue the
// separate template fan-out. This resolver produces the exact result shape
// loadResolvedNoteTemplates returns (and seeds its cache, so a follow-up
// caller — e.g. SessionDetailPanel — reuses the composed data instead of
// refetching).
export function resolveNoteTemplatesFromViewBlock(
  block:
    | {
        custom?: any[];
        ehr?: any[];
        ehrConfig?: {
          id?: string;
          ehrType?: string | null;
          status?: string | null;
          timeZone?: string | null;
        } | null;
        ehrEnabled?: boolean;
      }
    | null
    | undefined,
  {userId, companyId}: {userId?: string | null; companyId?: string | null} = {},
): any {
  const customTemplates = Array.isArray(block?.custom) ? block!.custom : [];
  const rawEhrTemplates = Array.isArray(block?.ehr) ? block!.ehr : [];
  const ehrEnabled = Boolean(block?.ehrEnabled);
  // Legacy snake_case EhrConfig field names (consumers read `ehr_type` and
  // isConfigActive reads `status`).
  const ehrConfig = block?.ehrConfig
    ? {
        id: block.ehrConfig.id,
        ehr_type: block.ehrConfig.ehrType ?? null,
        status: block.ehrConfig.status ?? null,
        timeZone: block.ehrConfig.timeZone ?? null,
      }
    : null;
  const ehrTemplates = ehrEnabled ? rawEhrTemplates : [];
  // "EhrConfig says active but no usable templates" is the degraded state
  // loadResolvedNoteTemplates flags (never cached).
  const ehrTemplatesFailed = ehrEnabled && ehrTemplates.length === 0;

  const value = {
    customTemplates,
    ehrTemplates,
    ehrConfig,
    ehrEnabledFromApi: ehrEnabled && ehrTemplates.length > 0,
    hasActiveEhr: ehrEnabled,
    ehrTemplatesFailed,
    ehrTemplatesError: ehrTemplatesFailed
      ? 'EHR templates returned empty'
      : null,
    allTemplates: mergeAllTemplates(
      customTemplates,
      ehrTemplates.map(ehrTemplateToScribeTemplate),
    ),
    fromCache: false,
  };

  if (userId && !ehrTemplatesFailed) {
    resultCache.set(cacheKey(userId, companyId), {
      value,
      cachedAt: Date.now(),
    });
  }

  return value;
}

// ── Test / sign-out helpers ──────────────────────────────────────
// Exposed so the auth context (and tests) can wipe the cache when a
// user signs out or switches accounts, so the next sign-in doesn't
// pick up the previous user's templates.
export function clearNoteTemplateCache() {
  resultCache.clear();
  inFlight.clear();
}
